import { Modal, Form, Input, Select } from 'antd';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';

import { getZonesAndDistricts } from 'services/zoning';

import BRButton from 'components/BRButton/BRButton';
import { notify } from 'components/Notify/Notify';

const CreateEditCodeModal = ({
  close,
  onSubmit,
  isEdit,
  selectedCode,
  hubStars = [],
  hubZones,
  zoneId,
  ...props
}) => {
  const [isDistrictsLoading, setIsDistrictsLoading] = useState(false);
  const [districts, setDistricts] = useState([]);

  const intl = useIntl();
  const [form] = Form.useForm();
  const selectedZoneId = Form.useWatch('zoneId', form);

  const fetchZonesAndDistricts = async () => {
    setIsDistrictsLoading(true);

    const selectedZone = selectedZoneId || zoneId;
    const cityId = hubZones.find((zone) => zone._id === selectedZone)?.city
      ?._id;

    try {
      const data = await getZonesAndDistricts(cityId);
      const zoneDistricts = data.find(
        (zone) => zone.zoneId === selectedZone
      )?.districts;

      setDistricts(zoneDistricts);
    } catch (error) {
      notify(error.message);
    } finally {
      setIsDistrictsLoading(false);
    }
  };

  const onFinish = (values) => {
    onSubmit(values);
    close();
  };

  const formatStarsOptions = () => {
    if (hubStars.length) {
      return hubStars.map((star) => ({
        value: star._id,
        label: `${star.profile?.firstName} ${star.profile?.lastName}`
      }));
    }
  };

  useEffect(() => {
    form.resetFields(['districtId']);

    if (selectedZoneId || zoneId) {
      fetchZonesAndDistricts();
    }
  }, [zoneId, selectedZoneId]);

  return (
    <Modal
      title={intl.formatMessage({
        id: `bosta_coding.codes_geofencing.create_edit_modal.${
          isEdit ? 'edit_title' : 'title'
        }`
      })}
      footer={
        <>
          <BRButton
            label={intl.formatMessage({ id: 'common.cancel' })}
            onClick={close}
          />
          <BRButton
            htmlType="submit"
            form="createEditCodeGeofencing"
            type="primary"
            label={intl.formatMessage({
              id: isEdit ? 'common.save' : 'common.create'
            })}
          />
        </>
      }
      {...props}
    >
      <Form
        onFinish={onFinish}
        initialValues={{ ...selectedCode }}
        name="createEditCodeGeofencing"
        form={form}
      >
        <Form.Item
          name="name"
          label={intl.formatMessage({
            id: 'bosta_coding.codes_geofencing.create_edit_modal.name_label'
          })}
          rules={[{ required: true }]}
        >
          <Input
            placeholder={intl.formatMessage({
              id: 'bosta_coding.codes_geofencing.create_edit_modal.name_placeholder'
            })}
          />
        </Form.Item>
        <Form.Item
          name="nameAr"
          label={intl.formatMessage({
            id: 'bosta_coding.codes_geofencing.create_edit_modal.name_ar_label'
          })}
        >
          <Input
            placeholder={intl.formatMessage({
              id: 'bosta_coding.codes_geofencing.create_edit_modal.name_ar_placeholder'
            })}
          />
        </Form.Item>
        <Form.Item
          name="l2SectorCode"
          label={intl.formatMessage({
            id: 'bosta_coding.district_mapping.labels.sector_code'
          })}
          rules={[{ required: true }]}
        >
          <Input
            placeholder={intl.formatMessage({
              id: 'bosta_coding.district_mapping.placeholder.enter_code'
            })}
          />
        </Form.Item>
        <Form.Item
          name="l2DistrictCode"
          label={intl.formatMessage({
            id: 'bosta_coding.district_mapping.labels.district_code'
          })}
          rules={[{ required: true }]}
        >
          <Input
            placeholder={intl.formatMessage({
              id: 'bosta_coding.district_mapping.placeholder.enter_code'
            })}
          />
        </Form.Item>
        {!zoneId && (
          <Form.Item
            name="zoneId"
            label={intl.formatMessage({
              id: 'bosta_coding.district_mapping.labels.zone'
            })}
            rules={[{ required: true }]}
          >
            <Select
              options={hubZones}
              fieldNames={{ label: 'name', value: '_id' }}
              showSearch
              showArrow
              optionFilterProp="name"
              placeholder={intl.formatMessage({
                id: 'common.select'
              })}
            />
          </Form.Item>
        )}
        <Form.Item
          name="districtId"
          label={intl.formatMessage({
            id: 'bosta_coding.district_mapping.labels.district'
          })}
          rules={[{ required: true }]}
        >
          <Select
            options={districts}
            fieldNames={{ label: 'name', value: '_id' }}
            showSearch
            showArrow
            optionFilterProp="name"
            loading={isDistrictsLoading}
            disabled={!districts?.length || isDistrictsLoading}
            placeholder={intl.formatMessage({
              id: 'common.select'
            })}
          />
        </Form.Item>
        <Form.Item
          name={['assignedStar', '_id']}
          label={intl.formatMessage({
            id: 'bosta_coding.codes_geofencing.create_edit_modal.star_label'
          })}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={formatStarsOptions()}
            placeholder={intl.formatMessage({
              id: 'bosta_coding.codes_geofencing.create_edit_modal.star_placeholder'
            })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateEditCodeModal;
